<template>
    <div class="p-3">
        <FullCalendar id="calendar" ref="calendar" :options="calendarOptions" />
    </div>

    <!-- Modal for VehicleEntry event -->
    <teleport to="body">
        <div class="modal fade" id="vehicleModal" tabindex="-1" aria-hidden="true" ref="vehicleModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content modal_wrapper">
                    <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                        <h3 class="modal-title">{{ $t("headers.vehicleDetail") }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="selectedEvent">
                            <dl class="row mb-0">
                                <dt class="col-sm-4">{{ $t("vehicleEntry.carrier") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.carrier }}</dd>

                                <dt class="col-sm-4">{{ $t("vehicleEntry.driver_name") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.driver_name }}</dd>

                                <dt class="col-sm-4">{{ $t("vehicleEntry.licencePlate") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.licence_plate }}</dd>

                                <dt class="col-sm-4">
                                    <span v-if="selectedEvent.actual_from">
                                        {{ $t("vehicleEntry.from") }}
                                    </span>
                                    <span v-else>{{ $t("vehicleEntry.expectedFrom") }}</span>
                                </dt>
                                <dd class="col-sm-8">
                                    <span v-if="selectedEvent.actual_from">
                                        {{ $d(new Date(selectedEvent.actual_from), 'short') }}
                                    </span>
                                    <span v-else-if="selectedEvent.expected_from">
                                        {{ $d(new Date(selectedEvent.expected_from), 'short') }}
                                    </span>
                                    <span v-else>-</span>
                                </dd>

                                <dt class="col-sm-4">{{ $t("vehicleEntry.to") }}</dt>
                                <dd class="col-sm-8">
                                    {{ selectedEvent.actual_to ? $d(new Date(selectedEvent.actual_to), 'short') : "-" }}
                                </dd>
                            </dl>

                            <h3 class="mb-3 mt-3 text-center">{{ $t("status.status") }}</h3>
                            <StatusSelector
                                :record="selectedEvent"
                                statusType="vehicleEntry"
                                modalId="vehicleModal"
                                :getUrl="`/${$route.params.slug}/statuses?model=vehicleentry`"
                                :submitUrl="`/${$route.params.slug}/vehicle-entries/${selectedEvent.id}/status/edit`"
                                @status-updated="updateCalendarEvent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>

    <!-- Modal for Guestbook event -->
    <teleport to="body">
        <div class="modal fade" id="guestbookModal" tabindex="-1" aria-hidden="true" ref="guestbookModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content modal_wrapper">
                    <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                        <h3 class="modal-title">{{ $t("headers.guestbookDetail") }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="selectedEvent">
                            <dl class="row mb-0">
                                <dt class="col-sm-4">{{ $t("guestbook.name") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.guest_name }}</dd>

                                <dt class="col-sm-4">{{ $t("guestbook.company") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.company }}</dd>

                                <dt class="col-sm-4">{{ $t("guestbook.cardNumber") }}</dt>
                                <dd class="col-sm-8">{{ selectedEvent.personal_card_number }}</dd>

                                <dt class="col-sm-4">{{ $t("guestbook.cardType") }}</dt>
                                <dd class="col-sm-8">
                                    {{ selectedEvent.personal_card_type ? $t("documentType." + selectedEvent.personal_card_type) : "-" }}
                                </dd>

                                <dt class="col-sm-4">
                                    <span v-if="selectedEvent.actual_from">
                                        {{ $t("guestbook.from") }}
                                    </span>
                                    <span v-else>{{ $t("guestbook.expectedFrom") }}</span>
                                </dt>
                                <dd class="col-sm-8">
                                    <span v-if="selectedEvent.actual_from">
                                        {{ $d(new Date(selectedEvent.actual_from), 'short') }}
                                    </span>
                                    <span v-else-if="selectedEvent.expected_from">
                                        {{ $d(new Date(selectedEvent.expected_from), 'short') }}
                                    </span>
                                    <span v-else>-</span>
                                </dd>

                                <dt class="col-sm-4">{{ $t("guestbook.to") }}</dt>
                                <dd class="col-sm-8">
                                    {{ selectedEvent.actual_to ? $d(new Date(selectedEvent.actual_to), 'short') : "-" }}
                                </dd>

                            </dl>
                            <h3 class="mb-3 text-center">{{ $t("status.status") }}</h3>
                            <StatusSelector
                                :record="selectedEvent"
                                statusType="guestbook"
                                modalId="guestbookModal"
                                :getUrl="`/${$route.params.slug}/statuses?model=guestbook`"
                                :submitUrl="`/${$route.params.slug}/guestbooks/${selectedEvent.id}/status/edit`"
                                @status-updated="updateCalendarEvent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
    import FullCalendar from "@fullcalendar/vue3";
    import dayGridPlugin from "@fullcalendar/daygrid";
    import interactionPlugin from "@fullcalendar/interaction";
    import timeGridPlugin from "@fullcalendar/timegrid";
    import multiMonthPlugin from "@fullcalendar/multimonth";
    import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
    import StatusSelector from "@/components/StatusSelector.vue";

    export default {
        components: {
            FullCalendar,
            StatusSelector,
        },
        data() {
            return {
                selectedEvent: null,
                vehicleModal: null,
                guestbookModal: null,
                calendarOptions: {
                    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, multiMonthPlugin],
                    initialView: "timeGridDay",
                    height: "auto",
                    locale: "cs",
                    firstDay: "1",
                    selectable: true,
                    editable: false,
                    nowIndicator: true,
                    headerToolbar: {
                        start: "today prev,next",
                        center: "title",
                        right: "timeGridDay timeGridWeek dayGridMonth multiMonthYear",
                    },
                    buttonText: {
                        today: "Dnes",
                        day: "Den",
                        week: "Týden",
                        month: "Měsíc",
                        year: "Rok",
                    },
                    allDayText: "",
                    views: {
                        timeGridWeek: {
                            slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                        },
                        timeGridDay: {
                            slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                        }
                    },
                    dayHeaderFormat: { weekday: "short" },
                    dayHeaderContent: (args) => {
                        const date = args.date;
                        const dayName = args.text.charAt(0).toUpperCase() + args.text.slice(1);
                        const dayNumber = date.getDate();

                        return {
                            html: `<div>${dayName}</div><div style="font-size: 1.3em; ">${dayNumber}</div>`
                        };
                    },
                    eventClick:this.handleEventClick,
                    events: [],
                }
            }
        },
        created() {
            this.getCalendarEvents();
        },
        mounted() {
            this.vehicleModal = new Modal(this.$refs.vehicleModal, { backdrop: "static"});
            this.guestbookModal = new Modal(this.$refs.guestbookModal, { backdrop: "static"});
        },
        methods: {
            getCalendarEvents() {
                const slug = this.$route.params.slug

                Promise.all([
                    this.$store.getters.api.get(`${slug}/vehicle-entries/calendar`),
                    this.$store.getters.api.get(`${slug}/guestbooks/calendar`)
                ])
                .then(res => {
                    const vehicleRes = res[0];
                    const guestbookRes = res[1];

                    this.calendarOptions.events = [
                        ...vehicleRes.data,
                        ...guestbookRes.data
                    ]
                })
                .catch(error => {
                    console.error("Error loading calendar events", error);
                });
            },

            handleEventClick(info) {
                const event = info.event;
                const type = event.extendedProps.type;

                this.selectedEvent = {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end,
                    ...event.extendedProps
                };
                this.openModal(type)
            },
            openModal(type) {
                if (type === "vehicle") {
                    this.vehicleModal.show()
                } else if (type === "guestbook") {
                    this.guestbookModal.show()
                }
            },
            updateCalendarEvent() {
                this.getCalendarEvents();
            }
        },
        watch: {
            '$route.params.slug'(newSlug) {
                this.getCalendarEvents();
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import "@/scss/abstracts/variables";

    #calendar :deep(.fc-toolbar-title) {
        color: $black;
        font-weight: bold;
        text-transform: capitalize;
    }

    #calendar :deep(.fc-timegrid-slot) {
        color: $black;
    }

    :deep(.fc-daygrid-day-top) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    :deep(.fc-daygrid-day-number) {
        color: $black;
        text-decoration: none;
    }
    :deep(.fc-col-header-cell-cushion) {
        text-decoration: none;
        color: $black;
    }
    :deep(.fc-timegrid-slot.fc-timegrid-slot-label.fc-timegrid-slot-minor) {
        border-top: none;
    }
    :deep(.fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-minor) {
        border-top: none;
    }
</style>